import { useState, useRef, useEffect } from 'react';
import { useWealthlaneCustomerApi } from '../../../../hooks';
import useChat from './useChat';
import AuthService from '../../../../services/authService';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../../../auth/authSlice';
import { selectAuthRoutesList } from '../../../../pages/configuration/routeSlice';

interface ChatMessage {
  sender: string;
  message: string;
}

const useAIChat = () => {
  const [inputText, setInputText] = useState(''); // State for text input
  const responseContainerRef = useRef<HTMLDivElement | null>(null); // For scrolling chat to the bottom
  const { handleRequest: customerName } = useWealthlaneCustomerApi();
  const authService = new AuthService();
  const isAuthenticated = useSelector(selectIsAuthenticated) || authService.isAuthenticated();
  const userAuthList = useSelector(selectAuthRoutesList);

  // Chat-related functionality
  const {
    loading, // Shared loading state for both text and audio interactions
    chatHistory,
    setChatHistory,
    fetchChatResponse, // Function to handle text input
    fetchAudioChatResponse, // Function to handle audio input
  } = useChat(getOrCreateSessionID());

  const scrollToBottom = () => {
    if (responseContainerRef.current) {
      responseContainerRef.current.scrollTo({
        top: responseContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(scrollToBottom, [chatHistory]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const handleApiCall = async () => {
    try {
      // console.log(authService.getToken());
      
      const auth_token = authService.getToken();
      await fetchChatResponse(inputText, auth_token); // Trigger text-based chat
      setInputText(''); // Clear the input after sending
    } catch (error) {
      console.error('Error processing text input:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleApiCall();
    }
  };

  const handleAudioApiCall = async (audioBlob: Blob) => {
    try {
      const auth_token = authService.getToken();
      await fetchAudioChatResponse(audioBlob, auth_token); // Trigger audio-based chat
    } catch (error) {
      console.error('Error processing audio input:', error);
    }
  };

  function getOrCreateSessionID() {
    let my_sessionId = sessionStorage.getItem('chat-session-id');
    if (!my_sessionId) {
      const guid = generateGUID();
      sessionStorage.setItem('chat-session-id', guid);
      my_sessionId = guid;
    }
    return my_sessionId;
  }

  function generateGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  return {
    inputText,
    chatHistory,
    setChatHistory,
    loading, // Unified loading state
    initialSetupLoading: !Boolean(userAuthList),
    responseContainerRef,
    scrollToBottom,
    handleInputChange,
    handleApiCall,
    handleAudioApiCall, // Expose audio handler for VoiceRecorder
    handleKeyPress,
  };
};

export default useAIChat;
